import React from 'react';

/**
 * Condensed read-only view of all wizard form sections.
 * Shows only fields that have values, organized by section.
 */

const currency = (v) => {
  if (v == null || v === '' || v === 0) return null;
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const label = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace('Pmi', 'PMI')
    .replace('Hsa Fsa', 'HSA / FSA')
    .replace('Cpa', 'CPA')
    .replace('K12', 'K-12')
    .replace('529', '529');

/* ── Section renderers ───────────────────────────────────────────── */

const renderIncome = (data) => {
  if (!data?.sources) return null;
  const filled = data.sources.filter((s) => s.name || s.amount);
  if (filled.length === 0) return null;
  return (
    <table className="table table-sm mb-0">
      <tbody>
        {filled.map((s, i) => (
          <tr key={i}>
            <td className="text-muted" style={{ width: '55%' }}>{s.name || `Source ${i + 1}`}</td>
            <td className="text-end fw-semibold">{currency(s.amount) || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const renderAuto = (data) => {
  if (!data) return null;
  const rows = [];
  ['auto_1', 'auto_2', 'auto_3'].forEach((key, i) => {
    const a = data[key];
    if (a?.amount) {
      rows.push(
        <tr key={key}>
          <td className="text-muted">Auto {i + 1} {a.is_lease ? '(Lease)' : '(Loan)'}</td>
          <td className="text-end fw-semibold">{currency(a.amount)}</td>
        </tr>
      );
    }
  });
  const other = ['gas_fuel', 'license_registration', 'parking_tolls', 'service_maintenance', 'auto_insurance', 'public_transportation'];
  other.forEach((key) => {
    if (data[key]) {
      rows.push(
        <tr key={key}>
          <td className="text-muted">{label(key)}</td>
          <td className="text-end fw-semibold">{currency(data[key])}</td>
        </tr>
      );
    }
  });
  return rows.length ? <table className="table table-sm mb-0"><tbody>{rows}</tbody></table> : null;
};

const renderMedical = (data) => {
  if (!data) return null;
  const pairs = [
    ['dental_copays', 'dental_insurance', 'dental_insurance_auto'],
    ['medical_copays', 'medical_insurance', 'medical_insurance_auto'],
    ['vision_copays', 'vision_insurance', 'vision_insurance_auto'],
    ['pharmacy_medications', 'prescription_insurance', 'prescription_insurance_auto'],
  ];
  const rows = [];
  pairs.forEach(([copay, ins, auto]) => {
    if (data[copay]) rows.push(<tr key={copay}><td className="text-muted">{label(copay)}</td><td className="text-end fw-semibold">{currency(data[copay])}</td></tr>);
    if (data[ins]) rows.push(<tr key={ins}><td className="text-muted">{label(ins)}{data[auto] ? ' ✦' : ''}</td><td className="text-end fw-semibold">{currency(data[ins])}</td></tr>);
  });
  if (data.alternative_health_care) rows.push(<tr key="alt"><td className="text-muted">Alternative Health Care</td><td className="text-end fw-semibold">{currency(data.alternative_health_care)}</td></tr>);
  return rows.length ? <table className="table table-sm mb-0"><tbody>{rows}</tbody></table> : null;
};

const renderInsurance = (data) => {
  if (!data) return null;
  const fields = [
    ['life_insurance', 'life_insurance_auto'],
    ['long_term_care', 'long_term_care_auto'],
    ['disability', 'disability_auto'],
    ['umbrella', 'umbrella_auto'],
    ['other_insurance', 'other_insurance_auto'],
  ];
  const rows = [];
  fields.forEach(([key, auto]) => {
    if (data[key]) rows.push(<tr key={key}><td className="text-muted">{label(key)}{data[auto] ? ' ✦' : ''}</td><td className="text-end fw-semibold">{currency(data[key])}</td></tr>);
  });
  return rows.length ? <table className="table table-sm mb-0"><tbody>{rows}</tbody></table> : null;
};

const renderSavings = (data) => {
  if (!data) return null;
  const fields = [
    ['retirement_savings', 'retirement_savings_auto'],
    ['annual_savings_cash', null],
    ['brokerage_taxable', null],
    ['education_savings_529', null],
    ['hsa_fsa', 'hsa_fsa_auto'],
    ['other_savings', null],
  ];
  const rows = [];
  fields.forEach(([key, auto]) => {
    if (data[key]) rows.push(<tr key={key}><td className="text-muted">{label(key)}{auto && data[auto] ? ' ✦' : ''}</td><td className="text-end fw-semibold">{currency(data[key])}</td></tr>);
  });
  return rows.length ? <table className="table table-sm mb-0"><tbody>{rows}</tbody></table> : null;
};

const renderCharity = (data) => {
  if (!data) return null;
  const rows = [];
  if (data.charitable_gifting) rows.push(<tr key="cg"><td className="text-muted">Charitable Gifting{data.is_qualified_charitable_distribution ? ' (QCD)' : ''}</td><td className="text-end fw-semibold">{currency(data.charitable_gifting)}</td></tr>);
  if (data.tithes_church) rows.push(<tr key="tc"><td className="text-muted">Tithes / Church</td><td className="text-end fw-semibold">{currency(data.tithes_church)}</td></tr>);
  return rows.length ? <table className="table table-sm mb-0"><tbody>{rows}</tbody></table> : null;
};

const renderSimple = (data, excludeKeys = []) => {
  if (!data) return null;
  const rows = Object.entries(data)
    .filter(([k, v]) => v != null && v !== '' && v !== 0 && v !== false && !excludeKeys.includes(k))
    .map(([k, v]) => (
      <tr key={k}>
        <td className="text-muted">{label(k)}</td>
        <td className="text-end fw-semibold">{currency(v)}</td>
      </tr>
    ));
  return rows.length ? <table className="table table-sm mb-0"><tbody>{rows}</tbody></table> : null;
};

/* ── Sections config ─────────────────────────────────────────────── */

const SECTIONS = [
  { key: 'income', title: 'Income', icon: 'bi-cash-coin', render: renderIncome },
  { key: 'housing', title: 'Housing', icon: 'bi-house', render: (d) => renderSimple(d) },
  { key: 'bills_utilities', title: 'Bills & Utilities', icon: 'bi-plug', render: (d) => renderSimple(d) },
  { key: 'auto_transport', title: 'Auto & Transport', icon: 'bi-car-front', render: renderAuto },
  { key: 'medical', title: 'Medical', icon: 'bi-heart-pulse', render: renderMedical },
  { key: 'insurance', title: 'Insurance', icon: 'bi-shield-check', render: renderInsurance },
  { key: 'entertainment_travel', title: 'Entertainment & Travel', icon: 'bi-airplane', render: (d) => renderSimple(d) },
  { key: 'personal_family_care', title: 'Personal & Family Care', icon: 'bi-people', render: (d) => renderSimple(d) },
  { key: 'kids_education', title: 'Kids & Education', icon: 'bi-mortarboard', render: (d) => renderSimple(d) },
  { key: 'pets', title: 'Pets', icon: 'bi-emoji-heart-eyes', render: (d) => renderSimple(d) },
  { key: 'charity', title: 'Charity', icon: 'bi-gift', render: renderCharity },
  { key: 'legal_professional', title: 'Legal / Professional', icon: 'bi-briefcase', render: (d) => renderSimple(d) },
  { key: 'savings', title: 'Savings', icon: 'bi-piggy-bank', render: renderSavings },
  { key: 'debts', title: 'Debts', icon: 'bi-credit-card', render: (d) => renderSimple(d) },
];

// Wizard bookkeeping stored alongside the sections (step-layout version,
// statement-import records) — not answers the client gave.
const isSectionKey = (key) => !key.startsWith('_') && key !== 'import';

const ImportedFilesNote = ({ files }) => {
  if (!files?.length) return null;
  const dates = files.map((f) => f.imported_at).filter(Boolean).sort();
  const when = dates.length
    ? new Date(dates[dates.length - 1]).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  return (
    <div className="col-12">
      <div className="alert alert-light border py-2 mb-0 small">
        <i className="bi bi-cloud-arrow-up me-2"></i>
        Starting figures imported from{' '}
        <strong>{files.map((f) => f.filename).join(', ')}</strong>
        {when ? ` (${when})` : ''}. The client could change them afterwards.
      </div>
    </div>
  );
};

const ClientFormView = ({ formData }) => {
  if (!formData || Object.keys(formData).filter(isSectionKey).length === 0) {
    return <p className="text-muted fst-italic">No form data submitted yet.</p>;
  }

  return (
    <div className="row g-3">
      <ImportedFilesNote files={formData.import?.files} />
      {SECTIONS.map(({ key, title, icon, render }) => {
        const sectionData = formData[key];
        const content = render(sectionData);
        if (!content) return null;
        return (
          <div className="col-md-6 col-xl-4" key={key}>
            <div className="card h-100">
              <div className="card-header py-2">
                <i className={`bi ${icon} me-2`}></i>
                <strong>{title}</strong>
              </div>
              <div className="card-body p-2">
                {content}
              </div>
            </div>
          </div>
        );
      })}
      <div className="col-12">
        <small className="text-muted">✦ = auto-deducted from wages</small>
      </div>
    </div>
  );
};

export default ClientFormView;
